import * as Sentry from "@sentry/react";
import { browserName, isMobile, mobileModel, mobileVendor, osName, osVersion } from "react-device-detect";
import { IOS_STORE_URL } from "./constants";
import { showTranslated, translate, type TranslatorKeys } from "./translator";
import { CollectionType, Favorite, FavoriteType, IdentifierType, IDType, MatchSmallElement } from "./types";
import {
  Ad,
  CardEvent,
  CardTypeEnum,
  ChangeEvent,
  EventType,
  EventTypeEnum,
  GoalEvent,
  GroupStageMinimal,
  Image,
  Language,
  LanguageLocaleEnum,
  League,
  LeagueCategoryEnum,
  LeagueListElement,
  LeagueListMinimum,
  MatchLeague,
  MatchListElement,
  MatchSmall,
  MatchTournamentGroup,
  MatchTournamentMinimal,
  MatchTournamentStage,
  MatchType,
  PlayerPositionEnum,
  ScopeEnum,
  SingleLeagueMatch,
  SportEnum,
  Tournament,
  TournamentGroup,
  TournamentMinimal,
  TournamentStage,
  Translation,
  User
} from "../client/api";

export function isLocalDev() {
  return !process.env.NODE_ENV || process.env.NODE_ENV === "development";
}

function isVeryLightHexColor(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.9;
}

export function getTeamHighlightColor(
  teamPrimary?: string | null,
  teamSecondary?: string | null
): { style?: { backgroundColor: string }; fallbackClass?: string } {
  const primaryRaw = teamPrimary?.trim();
  const secondaryRaw = teamSecondary?.trim();

  const isValidPrimary = !!primaryRaw && /^#([0-9a-fA-F]{6})$/.test(primaryRaw);
  const isValidSecondary = !!secondaryRaw && /^#([0-9a-fA-F]{6})$/.test(secondaryRaw);

  const usePrimary =
    isValidPrimary && primaryRaw != null && !isVeryLightHexColor(primaryRaw);
  const useSecondary =
    !usePrimary &&
    isValidSecondary &&
    secondaryRaw != null &&
    !isVeryLightHexColor(secondaryRaw);

  if (usePrimary && primaryRaw != null) {
    return { style: { backgroundColor: `${primaryRaw}33` } };
  }

  if (useSecondary && secondaryRaw != null) {
    return { style: { backgroundColor: `${secondaryRaw}33` } };
  }

  return { fallbackClass: "bg-primary-1 dark:bg-primary-50-dark" };
}

export function errorLogging(
  error: object | string,
  type?: Sentry.Severity
): void {
  if (isLocalDev()) {
    // eslint-disable-next-line no-console
    console.log(JSON.stringify(error));
    // eslint-disable-next-line no-console
    console.trace("Error at: ");
  } else {
    Sentry.addBreadcrumb({
      level: type ?? Sentry.Severity.Warning,
      data: { error }
    });
  }
}

export const HOUR_IN_MS = 60000 * 60;
export const MINUTE_IN_MS = 60000;
export const DAY_IN_MS = HOUR_IN_MS * 24;

/** Safe finite date check to avoid NaN times sneaking into logic */
export function isFiniteDate(date: Date): boolean {
  return Number.isFinite(date.getTime());
}

/** Normalize to local midnight for consistent day comparisons */
export function toLocalMidnight(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    12,
    0,
    0,
    0
  );
}

/** Convert timestamp to milliseconds. Handles numbers (seconds or ms) and ISO date strings */
export function toMs(value: number | string): number {
  if (typeof value === "number") {
    return value > 1e12 ? value : value * 1000;
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

export function playerPositionTranslatorKey(
  p: PlayerPositionEnum,
  league: League | LeagueListElement | LeagueListMinimum | boolean | undefined
): TranslatorKeys {
  const isCa5 = typeof league === "boolean"
    ? league
    : !!league &&
    league.subCategory?.leagueCategory === LeagueCategoryEnum.CALCIO_A_5;

  const suffix = isCa5 ? "CA5" : "";
  return `${p}${suffix}` as TranslatorKeys;
}

export function translatePlayerPosition(
  p: PlayerPositionEnum,
  league: League | LeagueListElement | LeagueListMinimum | boolean | undefined,
  l: LanguageLocaleEnum
): string {
  return translate(playerPositionTranslatorKey(p, league), l);
}

/** Format a Date into YYYYMMDD string (local calendar) */
export function formatYmdLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

/** Parse YYYYMMDD into a Date at local midnight; returns null if invalid */
export function parseYmdLocal(ymd: string): Date | null {
  if (!/^(\d{4})(\d{2})(\d{2})$/.test(ymd)) return null;
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(4, 6));
  const d = Number(ymd.slice(6, 8));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
    return null;
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
  return isFiniteDate(dt) ? dt : null;
}

export function removeDuplicateMatchDays(dates: Date[]): Date[] {
  return dates
    .map((date) => date.getTime())
    .filter((date, index, arr) => arr.indexOf(date) === index)
    .map((date) => new Date(date));
}

export function intlLang(l: LanguageLocaleEnum): string {
  return l === "de" ? "de-DE" : l === "it" ? "it-IT" : "en-US";
}

export function formatDate(
  date: Date | string,
  l: LanguageLocaleEnum,
  forInput = false,
  onlyTime = false,
  onlyDate = false,
  long = false
): string {
  const d = new Date(date);
  if (typeof d === "undefined") {
    return "";
  }
  if (forInput) {
    const iso = d.toISOString();
    if (onlyDate) return iso.split("T")[0];
    if (onlyTime) return iso.split("T")[1];
    return iso;
  }
  let options: Intl.DateTimeFormatOptions;
  if (onlyTime) {
    options = {
      hour: "2-digit",
      minute: "2-digit"
    };
    if (long) options.second = "2-digit";
    return d.toLocaleTimeString(intlLang(l), options);
  }
  if (onlyDate) {
    options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    };
    if (long) options.weekday = "long";
  } else {
    options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    };
  }

  return new Intl.DateTimeFormat(intlLang(l), options).format(d);
}

/**
 * Format time with AM/PM always on a separate line
 * Returns an object with time and period properties
 */
export function formatTimeWithSeparatePeriod(
  date: Date | string,
  l?: LanguageLocaleEnum
): { time: string; period: string } {
  const d = new Date(date);
  const hours = d.getHours();
  const minutes = d.getMinutes();

  if (l === "de" || l === "it") {
    const time24 = `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
    return { time: time24, period: "" };
  }

  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const period = hours < 12 ? "AM" : "PM";
  const time = `${hour12.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;

  return { time, period };
}


export function dayToNumber(date: Date): number {
  // Keep month 0-based to match numberToDay() converter
  return date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate();
}

export function numberToDay(date: number): Date {
  const year = Math.floor(date / 10000);
  const month = Math.floor((date - year * 10000) / 100);
  const day = Math.floor(date - year * 10000 - month * 100);
  return new Date(
    `${year}-${(month + 1).toString().padStart(2, "0")}-${day
      .toString()
      .padStart(2, "0")}`
  );
}

export function sameDay(date1: Date | number, date2: Date): boolean {
  if (typeof date1 === "number" || date1 === undefined) return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

type MatchBasic = {
  alternativeStartTime?: string | null;
  originalStartTime: string;
};

export function getMatchDate<T extends MatchBasic>(match: T): Date {
  return match.alternativeStartTime
    ? new Date(match.alternativeStartTime)
    : new Date(match.originalStartTime);
}

export function sortMatchesByTimeFast(
  a: MatchSmallElement,
  b: MatchSmallElement
): number {
  return a.startTime - b.startTime;
}

export function isGoalEvent(event: EventType | undefined): event is GoalEvent {
  return !!event && event.type === EventTypeEnum.GOAL;
}

export function isCardEvent(event: EventType | undefined): event is CardEvent {
  return !!event && event.type === EventTypeEnum.CARD;
}

export function isChangeEvent(
  event: EventType | undefined
): event is ChangeEvent {
  return !!event && event.type === EventTypeEnum.CHANGE;
}

const mailReg =
  /^[a-zA-Z0-9!#$%&'*+/=?^`{}|~_-]+[.a-zA-Z0-9!#$%&'*+/=?^`{}|~_-]*@[a-zA-Z0-9]+[._a-zA-Z0-9-]*\.[a-zA-Z0-9]+$/i;

export function checkMail(email: string): boolean {
  return mailReg.test(email);
}

export function isAdmin(user: User | undefined | null): boolean {
  return !!(
    user && !!user.scopes.find((scope) => scope.key === ScopeEnum.ADMIN)
  );
}

export function isInsiderPlus(user: User | undefined | null): boolean {
  return (
    isAdmin(user) ||
    (user
      ? !!user.scopes.find((sc) => sc.key === ScopeEnum.INSIDER_PLUS)
      : false)
  );
}

export function isInsider(
  user: User | undefined | null,
  teamID?: IDType
): boolean {
  return (
    isInsiderPlus(user) ||
    !!(user && user.groups.find((group) => !teamID || group.team.id === teamID))
  );
}

export function isTeamInsider(
  user: User | undefined | null,
  teamID?: IDType
): boolean {
  return isInsider(user, teamID);
}

export function isPremium(user: User | null | undefined): boolean {
  return !!user && user.pro;
}

export function isRegistered(user: User | undefined | null): boolean {
  return !!(user && !user.anonymous);
}

export function replaceOrAddResponseSingle<T extends CollectionType>(
  elem: T,
  storeArray: T[],
  cmpFunc?: (a: T, b: T) => boolean
): T[] {
  let found = false;
  return storeArray
    .map((e) => {
      if (cmpFunc ? cmpFunc(e, elem) : e.id === elem.id) {
        found = true;
        return elem;
      }
      return e;
    })
    .concat(found ? [] : [elem]);
}

export function replaceOrAddResponseMultiple<T extends CollectionType>(
  elems: T[],
  storeArray: T[]
): T[] {
  const found: IDType[] = [];
  return storeArray
    .map((e) => {
      const elem = elems.find((el) => el.id === e.id);
      if (elem) {
        found.push(elem.id);
        return elem;
      }
      return e;
    })
    .concat(elems.filter((e) => found.indexOf(e.id) === -1));
}

export function getChangeEvents(
  events: EventType[],
  teamId?: IDType
): ChangeEvent[] {
  return events.filter(
    (e) => isChangeEvent(e) && (!teamId || e.teamId === teamId)
  ) as ChangeEvent[];
}

export function getCardEvents(
  events: EventType[],
  teamId?: IDType,
  cardTypes?: CardTypeEnum[]
): CardEvent[] {
  return events.filter(
    (e) =>
      isCardEvent(e) &&
      (!teamId || e.teamId === teamId) &&
      (!cardTypes || cardTypes.indexOf(e.cardType.key) === -1)
  ) as CardEvent[];
}

export function getGoalEvents(
  events: EventType[],
  teamId?: IDType
): GoalEvent[] {
  return events.filter(
    (e) => isGoalEvent(e) && (!teamId || e.teamId === teamId)
  ) as GoalEvent[];
}

export function getDeviceInfo(): Record<string, string> {
  return {
    brand: isMobile ? mobileVendor : osName,
    buildNumber: "",
    model: isMobile ? mobileModel : osVersion,
    modelAlt: browserName,
    systemName: osName,
    systemVersion: osVersion,
    version: "",
    type: isMobile ? "Mobile" : "Desktop",
    userAgent: navigator.userAgent
  };
}

export function getStoreDownloadUrl(): string {
  const lowerOs = (osName || "").toLowerCase();
  const isApple =
    lowerOs.includes("mac") ||
    lowerOs.includes("ios") ||
    lowerOs.includes("iphone") ||
    lowerOs.includes("ipad");
  if (isApple) return IOS_STORE_URL;
  return "https://play.google.com/store/apps/details?id=com.clava&pli=1";
}

export function browserLang(): LanguageLocaleEnum {
  // Type-safe access to navigator.languages
  const navigatorWithLanguages = navigator as Navigator & {
    languages?: readonly string[];
  };
  const candidates: string[] =
    Array.isArray(navigatorWithLanguages.languages) &&
    navigatorWithLanguages.languages.length
      ? [...navigatorWithLanguages.languages]
      : [navigator.language];
  // Pick first supported base language from the browser preference list
  for (const cand of candidates) {
    const base = (cand || "").toLowerCase().split("-")[0];
    if (base === "it") return LanguageLocaleEnum.IT;
    if (base === "de") return LanguageLocaleEnum.DE;
    if (base === "en") return LanguageLocaleEnum.EN;
  }
  return LanguageLocaleEnum.EN;
}

export function toLanguageLocaleEnum(
  lang: Language | LanguageLocaleEnum | null | undefined
): LanguageLocaleEnum {
  if (!lang) return browserLang();
  if (typeof lang === "string") {
    return lang as LanguageLocaleEnum;
  }
  return lang.locale;
}

export const WEEKDAYS: Record<LanguageLocaleEnum, Record<number, string>> = {
  de: { 0: "So.", 1: "Mo.", 2: "Di.", 3: "Mi.", 4: "Do.", 5: "Fr.", 6: "Sa." },
  it: { 0: "Do.", 1: "Lu.", 2: "Ma.", 3: "Me.", 4: "Gi.", 5: "Ve.", 6: "Sa." },
  en: { 0: "Su.", 1: "Mo.", 2: "Tu.", 3: "We.", 4: "Th.", 5: "Fr.", 6: "Sa." }
};

function formatTwoDigits(value: number): string {
  return value.toString().padStart(2, "0");
}

function formatIsoDate(date: Date): string {
  return `${date.getFullYear()}-${formatTwoDigits(date.getMonth() + 1)}-${formatTwoDigits(
    date.getDate()
  )}`;
}

function formatWeekday(locale: LanguageLocaleEnum, date: Date): string {
  const map = WEEKDAYS[locale] ?? WEEKDAYS.en;
  const computed = map[date.getDay()];
  if (computed) return computed;
  try {
    return date.toLocaleDateString(locale, { weekday: "short" });
  } catch {
    return date.toLocaleDateString(undefined, { weekday: "short" });
  }
}

export function formatDayMonth(date: Date): string {
  return `${formatTwoDigits(date.getDate())}.${formatTwoDigits(date.getMonth() + 1)}`;
}

export function formatWeekdayDayMonth(date: Date, locale: LanguageLocaleEnum): string {
  return `${formatWeekday(locale, date)} ${formatDayMonth(date)}`;
}

function getRelativeLabel(date: Date, locale: LanguageLocaleEnum): string | null {
  const today = toLocalMidnight(new Date());
  const selectedLocalDate = toLocalMidnight(date);
  const diff = (selectedLocalDate.getTime() - today.getTime()) / DAY_IN_MS;

  if (diff > -0.5 && diff < 0.5) return translate("today", locale);
  if (diff >= 0.5 && diff < 1.5) return translate("tomorrow", locale);
  if (diff <= -0.5 && diff > -1.5) return translate("yesterday", locale);
  return null;
}

function formatDateLong(date: Date, locale: LanguageLocaleEnum): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "long",
      day: "numeric"
    }).format(date);
  } catch {
    return formatIsoDate(date);
  }
}

export function formatRelativeDateLabel(date: Date, locale: LanguageLocaleEnum): string {
  return getRelativeLabel(date, locale) ?? formatWeekdayDayMonth(date, locale);
}

export function formatRelativeOrFullDate(date: Date, locale: LanguageLocaleEnum): string {
  return getRelativeLabel(date, locale) ?? formatDateLong(date, locale);
}

export function sortEventsMinute(e1: EventType, e2: EventType): number {
  return (e1.minute ?? 99) - (e2.minute ?? 99);
}

export function getMainLeague<T extends League | LeagueListElement>(
  leagues: T[]
): T | undefined {
  let max = 0;
  leagues.forEach((l) => {
    if (l.main && l.year > max) max = l.year;
  });
  return leagues.find((l) => l.main && l.year === max);
}

export function emailOrTel(text: string): IdentifierType {
  if (text.match(/\+[0-9]+|[0-9]+$/gm)) {
    return "tel";
  }
  if (text.match(/[a-zA-Z@.]+/gm)) {
    return "mail";
  }
  return "both";
}

const parseAdDate = (value?: string | null): number | null => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

export function isAdActive(ad: Ad): boolean {
  if (ad?.paused) return false;

  const now = Date.now();
  const startsAt = parseAdDate(ad?.start);
  const endsAt = parseAdDate(ad?.stop);

  if (startsAt !== null && now < startsAt) return false;
  return !(endsAt !== null && now > endsAt);


}

export function pickAd(ads: Ad[], canShowMobileAds = true): Ad | null {
  if (!Array.isArray(ads) || ads.length === 0) return null;

  const activeAds = ads.filter(isAdActive);
  if (!activeAds.length) return null;

  const selector = activeAds.reduce<number[]>((prev, _current, index) => {
    const priority = activeAds[index]?.priority ?? 1;
    return prev.concat(Array(priority).fill(index));
  }, []);

  const maxRand = canShowMobileAds ? 100 : selector.length;
  const rand = Math.floor(Math.random() * maxRand);
  if (rand >= selector.length) return null;

  const selectedAdIndex = selector[rand];
  return activeAds[selectedAdIndex] ?? null;
}

export const MIN_AD_DISTANCE = 10;
export const MAX_AD_DISTANCE = 25;

export function shouldAdBePlaced(
  matchCounter: number,
  numOfAds: number
): boolean {
  if (numOfAds === 0) {
    if (matchCounter !== 0) {
      return true;
    }
  }
  return (
    matchCounter > MIN_AD_DISTANCE &&
    matchCounter > Math.random() * MAX_AD_DISTANCE
  );
}


export function generateAds<T>(
  items: T[],
  isMatch: (item: T) => boolean
): (T | string)[] {
  const result: (T | string)[] = [];
  let matchCounter = 0;
  let adIndex = 0;

  items.forEach((item) => {
    result.push(item);

    if (isMatch(item)) {
      matchCounter++;
      if (shouldAdBePlaced(matchCounter, adIndex)) {
        const adType = adIndex === 0 ? "priority" : "normal";
        result.push(`home-ad-${adIndex}-${adType}`);
        adIndex++;
        matchCounter = 0;
      }
    }
  });

  return result;
}

export function isAdString(item: unknown): item is string {
  return typeof item === "string" && item.startsWith("home-ad-");
}

export function isAdSenseAd(item: unknown): item is string {
  return typeof item === "string" && item === "adsense-ad";
}

export function setHead(title: string) {
  document.title = `Clava Sports - ${title}`;
}

/**
 * Return true if match done, flase if match not startet or the number of minutes passed since start
 * @param match
 * @param fullMatchLength
 */
export function matchStatus(
  match:
    | MatchLeague
    | MatchListElement
    | MatchSmallElement
    | MatchTournamentStage
    | MatchTournamentGroup,
  fullMatchLength?: number
): MATCH_STATUS {
  if ("startTime" in match) {
    return matchStatusDate(
      match.startTime,
      match.fullMatchLength,
      match.endTime
    );
  }
  const diffMinutes = calcMinute(match);
  const now = new Date().getTime();
  const endTime = new Date(match.endTime).getTime();
  if (diffMinutes < 0) {
    return "future";
  }
  if (now > endTime) {
    return "past";
  }
  const ml = fullMatchLength ?? fullMatchLengthMinutes(match);
  if (diffMinutes > ml) {
    return "ot";
  }
  return diffMinutes;
}

/**
 * Return past if match done, ot if in OT, future if match not startet or the number of minutes passed
 * @param startDate
 * @param matchLength
 * @param endTime
 */
export function matchStatusDate(
  startDate: number,
  matchLength: number,
  endTime: number
): MATCH_STATUS {
  const now = new Date().getTime();
  const diffMinutes = Math.ceil((now - startDate) / 60000);
  if (diffMinutes < 0) {
    return "future";
  }
  if (now > endTime) {
    return "past";
  }
  if (diffMinutes > matchLength) {
    return "ot";
  }
  return diffMinutes;
}

export function isLive(
  match: MatchLeague | MatchListElement | MatchSmallElement
): boolean {
  const stat =
    "startTime" in match
      ? matchStatusDate(match.startTime, match.fullMatchLength, match.endTime)
      : matchStatus(match);
  return typeof stat === "number" || stat === "ot";
}

export declare type MATCH_STATUS = "future" | "past" | number | "ot";

export function calcMinute(
  match:
    | MatchLeague
    | MatchListElement
    | MatchTournamentStage
    | MatchTournamentGroup
    | undefined
    | null
): number {
  if (match) {
    const now = new Date();
    return Math.ceil(
      (now.getTime() - getMatchDate(match).getTime()) / MINUTE_IN_MS
    );
  }
  return 0;
}

type MatchLengthEssential = {
  league: {
    matchSectionAmount: number;
    matchSectionDurationMinutes: number;
    matchSectionPauseDurationMinutes: number;
  };
};

export function fullMatchLengthMinutesRaw(
  matchSectionAmount: number,
  matchSectionDurationMinutes: number,
  matchSectionPauseDurationMinutes: number
) {
  return (
    matchLengthMinutesRaw(matchSectionAmount, matchSectionDurationMinutes) +
    (matchSectionAmount - 1) * matchSectionPauseDurationMinutes
  );
}

export function fullMatchLengthMinutes(
  match:
    | MatchLeague
    | MatchListElement
    | MatchLengthEssential
    | MatchTournamentGroup
    | MatchTournamentStage
    | undefined
    | null
): number {
  if (match) {
    if ("league" in match) {
      return fullMatchLengthMinutesRaw(
        match.league.matchSectionAmount,
        match.league.matchSectionDurationMinutes,
        match.league.matchSectionPauseDurationMinutes
      );
    }
    return fullMatchLengthMinutesCup(
      "tournamentStage" in match ? match.tournamentStage : match.tournamentGroup
    );
  }
  return -1;
}

export function fullMatchLengthMinutesCup(
  stage: GroupStageMinimal | TournamentStage | TournamentGroup
): number {
  return fullMatchLengthMinutesRaw(
    stage.matchSectionAmount,
    stage.matchSectionDurationMinutes,
    stage.matchSectionPauseDurationMinutes
  );
}

export function matchLengthMinutesRaw(
  matchSectionAmount: number,
  matchSectionDurationMinutes: number
) {
  return matchSectionAmount * matchSectionDurationMinutes;
}

export function getSectionsPassed(
  minute: number,
  sectionAmount: number,
  sectionDuration: number,
  pauseDuration: number
): number {
  let sectionsPassed = 0;
  for (let i = 1; i <= sectionAmount; i++) {
    if (
      minute >= fullMatchLengthMinutesRaw(i, sectionDuration, pauseDuration)
    ) {
      sectionsPassed = i;
    }
  }
  return sectionsPassed;
}

export function actualMatchMinute(
  minute: number,
  sectionAmount: number,
  sectionDuration: number,
  pauseDuration: number
): number {
  const duration = matchLengthMinutesRaw(sectionAmount, sectionDuration);
  const sectionsPassed = getSectionsPassed(
    minute,
    sectionAmount,
    sectionDuration,
    pauseDuration
  );
  const timePassed = fullMatchLengthMinutesRaw(
    sectionsPassed,
    sectionDuration,
    pauseDuration
  );
  const timePassedPlaying = timePassed - (sectionsPassed - 1) * pauseDuration;
  const inPause = minute - timePassed < pauseDuration;
  return Math.max(
    1,
    Math.min(
      duration + 1,
      inPause ? timePassedPlaying : minute - sectionsPassed * pauseDuration
    )
  );
}

export function isContentManager(user: User | undefined | null) {
  return (
    isAdmin(user) ||
    !!(user && !!user.scopes.find((scope) => scope.key === ScopeEnum.MANAGER))
  );
}

export const emptyTranslation: Translation = {
  id: -1,
  textDE: "",
  textIT: "",
  textEN: ""
};
export const emptyEmblem: Image = {
  id: "32de1ae1-ea19-4074-6420-57a8bf9ab600",
  url: "https://imagedelivery.net/l1rEUf9lP788c6Wug0LUJw/32de1ae1-ea19-4074-6420-57a8bf9ab600/public",
  uploadDate: new Date().toISOString(),
  aspectRatio: 1,
  uploadedById: -1
};

export function mapToMatchSmallSmall(match: MatchSmall): MatchSmallElement {
  const matchDate = new Date(match.startTime);
  return {
    id: match.id,
    goal1: match.goal1,
    goal2: match.goal2,
    startTime: matchDate.getTime(),
    endTime: new Date(match.endTime).getTime(),
    dayAsNumber: dayToNumber(matchDate),
    fullMatchLength: fullMatchLengthMinutesRaw(
      match.sectionAmount,
      match.sectionDuration,
      match.pauseDuration
    ),
    reducedMatchLength: matchLengthMinutesRaw(
      match.sectionAmount,
      match.sectionDuration
    ),
    sectionAmount: match.sectionAmount,
    sectionDuration: match.sectionDuration,
    pauseDuration: match.pauseDuration,
    type: match.matchType,
    leagueCupId: match.leagueTournamentId,
    leagueCupName: match.leagueTournamentName,
    cancelled: match.cancelled,
    sports: match.sports,
    team1: {
      id: match.team1?.id ?? -1,
      name: match.team1?.name ?? emptyTranslation,
      emblemUrl: match.team1?.emblemUrl ?? emptyEmblem.url,
      outOfCompetition: false,
      primaryColor: "#ffffff",
      secondaryColor: "#000000",
      officialName: "blabla"
    },
    team2: {
      id: match.team2?.id ?? -1,
      name: match.team2?.name ?? emptyTranslation,
      emblemUrl: match.team2?.emblemUrl ?? emptyEmblem.url,
      outOfCompetition: false,
      primaryColor: "#ffffff",
      secondaryColor: "#000000",
      officialName: "bla"
    },
    matchDay: match.matchDay
  };
}

export function toMatchTournamentMinimal(
  match: MatchTournamentGroup | MatchTournamentStage
): MatchTournamentMinimal {
  return {
    originalStartTime: match.originalStartTime,
    id: match.id,
    matchType: match.matchType as MatchType,
    team1: match.team1,
    team2: match.team2,
    team1Placeholder: match.team1Placeholder,
    team2Placeholder: match.team2Placeholder,
    goal1: match.goal1,
    goal2: match.goal2,
    alternativeStartTime: match.alternativeStartTime,
    endTime: match.endTime,
    cancelled: match.cancelled,
    nextMatchId: match.nextMatchId,
    location: match.location,
    firstHalfEndTime: match.firstHalfEndTime,
    secondHalfStartTime: match.secondHalfStartTime,
    matchBoardLoaded: match.matchBoardLoaded,
    kickedOff: match.kickedOff
  };
}

export function mapToMatchSmall(
  match:
    | MatchListElement
    | MatchLeague
    | MatchTournamentGroup
    | MatchTournamentStage
    | MatchTournamentMinimal
    | SingleLeagueMatch,
  leagueOrCup: League | LeagueListElement | Tournament | TournamentMinimal
): MatchSmallElement {
  const matchDate = getMatchDate(match);
  const startTime = matchDate.getTime();
  const endTime = new Date(match.endTime).getTime();
  const length = Math.floor((endTime - startTime) / 60000);
  return {
    id: match.id,
    goal1: match.goal1,
    goal2: match.goal2,
    startTime,
    endTime,
    dayAsNumber: dayToNumber(matchDate),
    fullMatchLength: leagueOrCup
      ? fullMatchLengthMinutesRaw(
        leagueOrCup.matchSectionAmount,
        leagueOrCup.matchSectionDurationMinutes,
        leagueOrCup.matchSectionPauseDurationMinutes
      )
      : length,
    reducedMatchLength: leagueOrCup
      ? matchLengthMinutesRaw(
        leagueOrCup.matchSectionAmount,
        leagueOrCup.matchSectionDurationMinutes
      )
      : length,
    sectionAmount:
      "tournamentStage" in match
        ? match.tournamentStage.matchSectionAmount
        : leagueOrCup.matchSectionAmount,
    sectionDuration:
      "tournamentStage" in match
        ? match.tournamentStage.matchSectionDurationMinutes
        : leagueOrCup.matchSectionDurationMinutes,
    pauseDuration:
      "tournamentStage" in match
        ? match.tournamentStage.matchSectionPauseDurationMinutes
        : leagueOrCup.matchSectionPauseDurationMinutes,
    type: match.matchType as MatchType,
    leagueCupId: leagueOrCup ? leagueOrCup.id : -1,
    leagueCupName: leagueOrCup ? leagueOrCup.name : emptyTranslation,
    cancelled: "cancelled" in match ? match.cancelled : false,
    sports: leagueOrCup ? leagueOrCup.sports : SportEnum.SOCCER,
    team1: {
      id: match.team1?.id ?? -1,
      name: match.team1
        ? match.team1.name
        : "team1Placeholder" in match
          ? {
            id: -1,
            text: match.team1Placeholder,
            textDE: match.team1Placeholder,
            textIT: match.team1Placeholder,
            textEN: match.team1Placeholder
          }
          : emptyTranslation,
      emblemUrl: match.team1 ? match.team1.emblem.url : emptyEmblem.url,
      outOfCompetition: false,
      primaryColor: "#ffffff",
      secondaryColor: "#000000",
      officialName: "bla"
    },
    team2: {
      id: match.team2?.id ?? -1,
      name: match.team2
        ? match.team2.name
        : "team1Placeholder" in match
          ? {
            id: -1,
            text: match.team2Placeholder,
            textDE: match.team2Placeholder,
            textIT: match.team2Placeholder,
            textEN: match.team2Placeholder
          }
          : emptyTranslation,
      emblemUrl: match.team2 ? match.team2.emblem.url : emptyEmblem.url,
      outOfCompetition: false,
      primaryColor: "#ffffff",
      secondaryColor: "#000000",
      officialName: "bla"
    },
    matchDay: "matchDay" in match ? match.matchDay : 0
  };
}

export function nameRegexTest(name: string): boolean {
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    if (!(char >= 32 && char <= 50000)) {
      return false;
    }
  }
  return true;
}

export function getPlayerRatingColor(rating: number | null): string {
  if (rating === null) {
    return "bg-no-rating";
  }

  if (rating >= 8.5) {
    return "bg-rating-8";
  }
  if (rating >= 7.1) {
    return "bg-rating-7";
  }
  if (rating >= 5.6) {
    return "bg-rating-6";
  }
  return "bg-rating-5";
}

export function calculateAge(birthdate: string | null | undefined): number | null {
  if (!birthdate) return null;
  const birthDate = new Date(birthdate);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function sortAlphabetically<T>(
  array: T[],
  getText: (item: T) => string
): T[] {
  return [...array].sort((a, b) => {
    const textA = getText(a);
    const textB = getText(b);
    return textA.localeCompare(textB, undefined, { sensitivity: "base" });
  });
}

export function displayLeagueName(league: League | LeagueListElement, l: LanguageLocaleEnum) {
  const leagueName = showTranslated(league.name, l).trim();
  const subCatName = showTranslated(league.subCategory.name, l).trim();
  if (leagueName.includes(subCatName)) {
    return leagueName;
  }
  return `${subCatName} - ${leagueName}`;
}


export function isFavorite(
  favorites: Favorite[],
  id: IDType,
  type: FavoriteType,
  team1Id?: IDType,
  team2Id?: IDType
): boolean {
  return !!favorites.find(
    fav =>
      (fav.id === id && fav.type === type) ||
      (type === "match" &&
        ((fav.type === "team" && fav.id === team1Id) ||
          (fav.type === "team" && fav.id === team2Id)))
  );
}
